import React from "react";
import { Button } from "components/ui/button";
import { CaretDown } from "@phosphor-icons/react";

/**
 * Props for LazyLoadTrigger component
 */
export interface LazyLoadTriggerProps {
  /** Ref to attach IntersectionObserver to */
  observerRef: React.RefObject<HTMLDivElement>;

  /** Whether more data exists to load */
  hasMore: boolean;

  /** Whether currently loading more data */
  isLoadingMore: boolean;

  /** Custom loading message */
  loadingMessage?: string;

  /** Custom "all loaded" message */
  allLoadedMessage?: string;

  /** Total number of items currently loaded (for display) */
  totalCount?: number;

  /** Total number of items available on server (for "X of Y" display) */
  totalAvailable?: number | null;

  /** Custom loading component (overrides default spinner + message) */
  customLoadingComponent?: React.ReactNode;

  /** Manual load more function (fallback if lazy loading fails) */
  onManualLoadMore?: () => void;
}

/**
 * Reusable component for lazy loading trigger and loading indicators
 *
 * Renders:
 * - Observer target div (invisible, triggers scroll detection)
 * - Loading spinner when fetching more data
 * - "All loaded" message when no more data exists
 *
 * @example
 * <LazyLoadTrigger
 *   observerRef={observerTarget}
 *   hasMore={hasMore}
 *   isLoadingMore={isLoadingMore}
 *   loadingMessage="Loading more contacts..."
 *   allLoadedMessage="All contacts loaded"
 *   totalCount={contacts.length}
 * />
 */
export const LazyLoadTrigger: React.FC<LazyLoadTriggerProps> = ({
  observerRef,
  hasMore,
  isLoadingMore,
  allLoadedMessage = "All items loaded",
  totalCount,
  totalAvailable,
  onManualLoadMore,
}) => {
  console.log("🎯 [LazyLoadTrigger] Rendering with:", {
    hasMore,
    totalCount,
    totalAvailable,
    isLoadingMore,
    observerRefAttached: !!observerRef.current,
  });

  return (
    <>
      {/* Observer target - always rendered to keep observer attachment valid */}
      {/* Hidden when hasMore=false to prevent unnecessary triggers */}
      {/* Small height (4px) works perfectly with rootMargin: '800px' in observer */}
      <div
        ref={observerRef}
        className="h-1"
        style={{ visibility: hasMore ? "visible" : "hidden" }}
        data-lazy-trigger="true"
      />

      {/* Manual load more button (fallback for lazy loading) */}
      {hasMore && !isLoadingMore && onManualLoadMore && (
        <div className="flex flex-col items-center gap-2 py-6">
          {totalCount !== undefined &&
            totalAvailable !== undefined &&
            totalAvailable !== null && (
              <div className="text-neutral-secondary-mid text-sm">
                {totalCount} van {totalAvailable} geladen
              </div>
            )}
          <Button
            onClick={onManualLoadMore}
            variant="outline"
            size="sm"
            className="gap-2"
          >
            <CaretDown size={16} weight="bold" />
            Laad
            {totalAvailable && totalCount
              ? Math.min(30, totalAvailable - totalCount)
              : 30}
            meer records
          </Button>
        </div>
      )}

      {/* Loading indicator */}
      {hasMore && isLoadingMore && (
        <div className="text-neutral-secondary-mid py-6 text-center text-sm">
          Laden...
        </div>
      )}

      {/* End of list indicator */}
      {!hasMore && totalCount !== undefined && totalCount > 0 && (
        <div className="text-neutral-secondary-mid py-8 text-center text-sm">
          {allLoadedMessage}
        </div>
      )}
    </>
  );
};

export default LazyLoadTrigger;
